import {
  AbsoluteAddressDropdown,
  AdminInput,
  AdminSpinner,
  Button,
  Div,
  ErrorToast,
  Modal,
  PropsModal,
  SuccessToast,
  assignChangeAddressVDriveDriver,
  lightTheme,
  reportError,
} from '@vgrubs/components';
import { Order } from 'components/src/types/order.types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';

export const ChangeAddress = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setData,
}: PropsModal & {
  modalIsOpen: number | null;
  setData: Dispatch<SetStateAction<Order | null>>;
}) => {
  const [address, setAddress] = useState('');
  const [aptNumber, setAptNumber] = useState('');
  const [zipCode, setZipCode] = useState('');

  async function handleChangeAddress() {
    if (!address) return ErrorToast('Please Choose Address');

    const body = {
      address,
      aptNumber,
      zipCode,
    };

    try {
      setLoading?.(true);
      const { data } = await assignChangeAddressVDriveDriver(
        modalIsOpen as number,
        body
      );
      setData(data.data);
      closeModal?.();
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading?.(false);
    }
  }

  useEffect(() => {
    setAddress('');
    setAptNumber('');
    setZipCode('');
  }, [modalIsOpen]);

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      zIndex={110}
      header={'Change Address'}
    >
      <LocationSearchInput
        address={address}
        setAddress={setAddress}
        setZipCode={setZipCode}
      />

      <AdminInput
        $textCenter
        placeholder="Enter here"
        label="Apt. Number"
        value={aptNumber}
        onChange={({ target }: { target: { value: string } }) =>
          setAptNumber(target.value)
        }
      />
      <Button $mt="24" $w="100%" $selected onClick={handleChangeAddress}>
        Confirm
      </Button>
      <Button $w="100%" $mt="16" $selected $gray onClick={() => closeModal?.()}>
        Cancel
      </Button>
    </Modal>
  );
};

const LocationSearchInput = ({
  address,
  setAddress,
  setZipCode,
}: {
  address: string;
  setAddress: (address: string) => void;
  setZipCode: (zipCode: string) => void;
}) => {
  const handleChange = (address: string) => {
    setAddress(address);
  };

  const handleSelect = (address: string, placeId: string) => {
    setAddress(address);

    // Get detailed place information including zip code
    if (window.google && window.google.maps) {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      service.getDetails(
        {
          placeId: placeId,
          fields: ['address_components'],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            // Extract zip code from address components
            const zipCodeComponent = place.address_components?.find(
              (component) => component.types.includes('postal_code')
            );

            if (zipCodeComponent) {
              setZipCode(zipCodeComponent.long_name);
            }
          }
        }
      );
    }
  };

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <Div $relative $mb="24">
          <AdminInput
            $textCenter
            {...getInputProps({
              label: 'New Delivery Address',
              placeholder: 'Choose Address',
            })}
          />
          {suggestions.length ? (
            <AbsoluteAddressDropdown $box $p="8px">
              {loading && <AdminSpinner absolute small />}
              {suggestions.map((suggestion, i) => {
                const style = suggestion.active
                  ? { color: lightTheme.primary, cursor: 'pointer' }
                  : { backgroundColor: '', cursor: 'pointer' };
                return (
                  <Div
                    {...getSuggestionItemProps(suggestion, {
                      style,
                    })}
                    key={i}
                    $p="8px"
                  >
                    <span>{suggestion.description}</span>
                  </Div>
                );
              })}
            </AbsoluteAddressDropdown>
          ) : null}
        </Div>
      )}
    </PlacesAutocomplete>
  );
};
